import { Header, renderServiceTabs, AppVersionFooter, FloatingNav, BackToTopButton, initBackToTop } from '../../components';
import { setState, navigateTo, getState } from '../../store';
import { getOutletsByType, getSpaConfig } from '../../api';
import { getCurrentLanguage, formatLocalDate, t } from '../../i18n';
import { renderCarousel } from '../../components/carousel';
import { term } from '../../miniapp-config';
import type { MiniAppService } from '@revio/shared';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

interface LocalizedString { th: string; en: string; zh: string; ru?: string; ja?: string; ko?: string; }
interface AppointmentItem {
  code: string;
  name: LocalizedString | string;
  duration?: number;
  price?: number;
  description?: LocalizedString | string;
  desc?: string;
  speaker?: string;
  cpdHours?: number;
  totalSeats?: number;
  bookedSeats?: number;
  date?: string;
  location?: string;
  vatType?: 'vat' | 'non_vat' | string;
  serviceChargeType?: 'service' | 'non_service' | string;
}

interface AppointmentOutlet {
  id?: string;
  name?: LocalizedString | string;
  description?: LocalizedString | string;
  enabled?: boolean;
  openTime?: string;
  closeTime?: string;
  slotIntervalMinutes?: number;
  maxBedsPerSlot?: number;
  images?: Array<{ url: string; thumbUrl?: string }>;
  items?: AppointmentItem[];
  treatments?: AppointmentItem[];
}

let appointmentOutlets: AppointmentOutlet[] = [];
let activeOutletId: string | null = null;
let outletsLoaded = false;

const DEFAULT_ITEMS: AppointmentItem[] = [
  {
    code: 'STANDARD_SERVICE',
    name: {
      th: 'บริการมาตรฐาน',
      en: 'Standard Service',
      zh: '标准服务',
      ru: 'Стандартная услуга',
      ja: '標準サービス',
      ko: '기본 서비스',
    },
    duration: 60,
    price: 1000,
  },
];

const DEFAULT_TIME_SLOTS = ['10:00', '11:00', '13:00', '14:00', '15:00', '16:00', '17:00'];

let selectedItemCode = 'STANDARD_SERVICE';
let selectedDateStr = formatLocalDate();
let selectedTimeSlot = '10:00';
let paxCount = 1;
let noteInput = '';

function getLocalizedText(val: any, lang: string, fallback: string = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return val[lang] || val.en || val.th || val.zh || val.ru || val.ja || val.ko || fallback;
  }
  return fallback;
}

export function renderAppointmentEngine(service: MiniAppService): string {
  const lang = getCurrentLanguage();
  const serviceId = service?.id || 'appointments';

  const editBookingId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('editBookingId') : null;
  let editBookingData: any = null;
  if (editBookingId) {
    try {
      editBookingData = JSON.parse(sessionStorage.getItem('editBookingData') || '{}');
      const appt = editBookingData.customFields?.appointmentDetails || editBookingData.spaDetails;
      if (appt) {
        if (appt.bookingDate) selectedDateStr = appt.bookingDate;
        if (appt.timeSlot) selectedTimeSlot = appt.timeSlot;
        if (appt.treatmentCode || appt.itemCode) selectedItemCode = appt.treatmentCode || appt.itemCode;
        if (appt.pax) paxCount = appt.pax;
        if (appt.outletId && appointmentOutlets.some(o => o.id === appt.outletId)) activeOutletId = appt.outletId;
      }
    } catch (_) {}
  }

  const activeOutlet = appointmentOutlets.find(o => o.id === activeOutletId) || appointmentOutlets[0] || {};
  const currentItems = [...(activeOutlet.items || activeOutlet.treatments || DEFAULT_ITEMS)];

  if (selectedItemCode && !currentItems.some(i => i.code === selectedItemCode)) {
    selectedItemCode = currentItems[0]?.code || 'STANDARD_SERVICE';
  }

  const selectedItem = currentItems.find(i => i.code === selectedItemCode) || currentItems[0] || DEFAULT_ITEMS[0];
  const totalPrice = (selectedItem.price || 0) * paxCount;
  const outletImages = activeOutlet.images || [];

  const itemTerm = term('item', serviceId, t('termItem'));
  const providerTerm = term('provider', serviceId, t('termProvider'));
  const locationTerm = term('location', serviceId, t('termLocation'));
  const customerTerm = term('customer', serviceId, t('termCustomer'));

  const svcTitle = getLocalizedText(service.label, lang, t('appName'));
  const outletNameText = getLocalizedText(activeOutlet.name, lang, svcTitle);
  const outletDescText = getLocalizedText(activeOutlet.description, lang, '');
  const openHoursText = activeOutlet.openTime && activeOutlet.closeTime ? `${activeOutlet.openTime} – ${activeOutlet.closeTime}` : '09:00 – 18:00';

  return `
    ${Header(outletNameText, false, true)}
    ${renderServiceTabs('service', serviceId)}

    <div class="page-container" id="appointment-content" style="padding:16px;padding-bottom:100px;">
      ${editBookingId ? `
        <div style="margin-bottom: 12px; padding: 10px 14px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 6px; font-size: 13px; color: #856404; display: flex; justify-content: space-between; align-items: center;">
          <div><strong>✏️ ${t('editModeBanner')}</strong> (${editBookingData?.confirmationNumber || editBookingId})</div>
          <button type="button" onclick="sessionStorage.removeItem('editBookingId');sessionStorage.removeItem('editBookingData');window.location.reload();" style="border:none;background:none;color:#dc3545;font-size:12px;cursor:pointer;text-decoration:underline;">${t('cancelEdit')}</button>
        </div>
      ` : ''}

      ${appointmentOutlets.length > 1 ? `
        <div id="appointment-outlets-scroll" style="display:flex;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;">
          ${appointmentOutlets.map((o) => `
            <button 
              type="button"
              onclick="window.switchAppointmentOutlet?.('${o.id}')"
              style="flex-shrink:0;padding:8px 14px;border-radius:20px;font-size:13px;font-weight:600;border:${o.id === activeOutletId ? '1.5px solid #0284c7' : '1px solid #e0e0e0'};background:${o.id === activeOutletId ? '#e0f2fe' : '#fff'};color:${o.id === activeOutletId ? '#0369a1' : '#555'};cursor:pointer;white-space:nowrap;"
            >
              📍 ${getLocalizedText(o.name, lang, locationTerm)}
            </button>
          `).join('')}
        </div>
      ` : ''}

      ${outletImages.length > 0 ? `
        <div class="card" style="background:#fff;border-radius:12px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
          <div id="appointment-carousel-container" style="position:relative;width:100%;height:180px;background:#f5f5f5;"></div>
          <div style="padding:14px;">
            <div style="font-size:16px;font-weight:700;color:#111;margin-bottom:4px;">${outletNameText}</div>
            ${outletDescText ? `<div style="font-size:12px;color:#666;line-height:1.5;margin-bottom:8px;">${outletDescText}</div>` : ''}
            <div style="font-size:12px;color:#0284c7;font-weight:500;">🕒 ${openHoursText}</div>
          </div>
        </div>
      ` : ''}

      <!-- Date & Time Slot Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="font-size:14px;font-weight:700;color:#111;margin-bottom:12px;">📅 ${t('selectTimeSlot')}</div>
        
        <div 
          id="appointment-date-trigger" 
          style="display:flex;align-items:center;justify-content:space-between;padding:10px 14px;border:1px solid #e0e0e0;border-radius:8px;background:#f9f9f9;cursor:pointer;margin-bottom:12px;"
        >
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-size:16px;">🗓️</span>
            <span id="appointment-date-display" style="font-size:13px;font-weight:600;color:#333;">${selectedDateStr}</span>
          </div>
          <span id="appointment-date-toggle-icon" style="font-size:12px;color:#888;transition:transform 0.2s;">▼</span>
        </div>
        <div id="appointment-picker-container" class="litepicker-inline collapsed" style="margin-bottom:12px;"></div>

        <div style="display:grid;grid-template-columns:repeat(4, 1fr);gap:8px;">
          ${DEFAULT_TIME_SLOTS.map(slot => `
            <button 
              type="button"
              id="appointment-time-chip-${slot.replace(':', '_')}"
              onclick="window.selectAppointmentTimeSlot?.('${slot}')"
              style="padding:8px 4px;border-radius:8px;font-size:12px;font-weight:600;text-align:center;border:${selectedTimeSlot === slot ? '1px solid #0284c7' : '1px solid #e0e0e0'};background:${selectedTimeSlot === slot ? '#0284c7' : '#fff'};color:${selectedTimeSlot === slot ? '#fff' : '#333'};cursor:pointer;"
            >
              ${slot}
            </button>
          `).join('')}
        </div>
      </div>

      <!-- Items / Service Selection -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="font-size:14px;font-weight:700;color:#111;margin-bottom:12px;">🩺 ${itemTerm}</div>
        <div style="display:flex;flex-direction:column;gap:10px;">
          ${currentItems.map(item => `
            <div 
              class="appointment-item-card"
              data-code="${item.code}"
              onclick="window.selectAppointmentItem?.('${item.code}')"
              style="display:flex;justify-content:space-between;align-items:center;padding:12px;border-radius:8px;border:${selectedItemCode === item.code ? '1.5px solid #0284c7' : '1px solid #e0e0e0'};background:${selectedItemCode === item.code ? '#f0f9ff' : '#fff'};cursor:pointer;"
            >
              <div style="flex:1;min-width:0;padding-right:12px;">
                <div style="font-size:14px;font-weight:600;color:#111;line-height:1.3;margin-bottom:4px;">${getLocalizedText(item.name, lang, item.code)}</div>
                ${item.speaker ? `<div style="font-size:11px;color:#444;margin-bottom:2px;">🎤 ${item.speaker}</div>` : ''}
                <div style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin-top:4px;">
                  ${item.cpdHours ? `<span style="font-size:10px;font-weight:600;background:#fef3c7;color:#b45309;padding:2px 6px;border-radius:4px;">🎓 CPD ${item.cpdHours} ชม.</span>` : ''}
                  ${item.duration ? `<span style="font-size:11px;color:#666;">⏱️ ${item.duration} ${t('minutesUnit')}</span>` : ''}
                  ${item.totalSeats ? `
                    <span style="font-size:10px;font-weight:600;background:${Math.max(0, item.totalSeats - (item.bookedSeats || 0)) <= 5 ? '#ffe4e6' : '#dcfce7'};color:${Math.max(0, item.totalSeats - (item.bookedSeats || 0)) <= 5 ? '#e11d48' : '#15803d'};padding:2px 6px;border-radius:4px;">
                      🪑 เหลือ ${Math.max(0, item.totalSeats - (item.bookedSeats || 0))} ที่นั่ง
                    </span>
                  ` : ''}
                </div>
              </div>
              <div style="font-size:14px;font-weight:700;color:#0284c7;text-align:right;white-space:nowrap;">
                <div>${item.price && item.price > 0 ? `฿${item.price.toLocaleString()}` : (lang === 'th' ? 'ฟรี' : 'Free')}</div>
                ${item.price && item.price > 0 ? `
                  <div style="font-size:10px;font-weight:500;color:${item.vatType === 'non_vat' ? '#b45309' : '#15803d'};margin-top:2px;">
                    ${item.vatType === 'non_vat' ? (lang === 'th' ? 'ไม่มี VAT' : 'Non-VAT') : (lang === 'th' ? 'รวม VAT' : 'VAT Incl.')}
                  </div>
                ` : ''}
              </div>
            </div>
          `).join('')}
        </div>
      </div>

      <!-- Pax & Customer Count -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <div>
            <div style="font-size:14px;font-weight:700;color:#111;">👥 ${customerTerm}</div>
            <div style="font-size:11px;color:#888;">${t('guests') || 'Quantity'}</div>
          </div>
          <div style="display:flex;align-items:center;gap:12px;background:#f5f5f5;border-radius:20px;padding:4px 10px;">
            <button onclick="window.changeAppointmentPax?.(-1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#0284c7;cursor:pointer;">-</button>
            <span id="appointment-pax-display" style="font-size:14px;font-weight:600;min-width:20px;text-align:center;">${paxCount}</span>
            <button onclick="window.changeAppointmentPax?.(1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#0284c7;cursor:pointer;">+</button>
          </div>
        </div>
      </div>

      <!-- Price & Checkout Button -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);display:flex;justify-content:space-between;align-items:center;">
        <div>
          <div style="font-size:12px;color:#666;">${t('totalAmount')}</div>
          <div style="font-size:11px;color:#888;">${lang === 'th' ? 'ชำระ ณ สถานที่บริการ' : 'Pay at Venue'}</div>
        </div>
        <div id="appointment-total-price" style="font-size:20px;font-weight:700;color:#0284c7;">
          ${totalPrice > 0 ? `฿${totalPrice.toLocaleString()}` : (lang === 'th' ? 'ฟรี' : 'Free')}
        </div>
      </div>

      <button 
        type="button" 
        class="btn btn-primary btn-block btn-lg" 
        onclick="window.proceedAppointmentToGuestInfo?.()"
      >
        ${t('next')} ›
      </button>

      ${AppVersionFooter()}
    </div>

    ${BackToTopButton('appointment')}
    ${FloatingNav()}
  `;
}

export function initAppointmentEngine(service: MiniAppService): void {
  const serviceId = service?.id || 'appointments';
  const outletType = service?.outletType || 'clinic';

  if (!outletsLoaded) {
    getOutletsByType(outletType).then((outlets) => {
      outletsLoaded = true;
      if (Array.isArray(outlets) && outlets.length > 0) {
        appointmentOutlets = outlets;
        activeOutletId = outlets[0].id || null;
        if (outlets[0].items && outlets[0].items.length > 0) {
          selectedItemCode = outlets[0].items[0].code;
        } else if (outlets[0].treatments && outlets[0].treatments.length > 0) {
          selectedItemCode = outlets[0].treatments[0].code;
        }
        (window as any).renderApp?.();
      }
    }).catch(err => {
      console.error('[AppointmentEngine] Error loading outlets:', err);
      outletsLoaded = true;
    });
  }

  // Render Carousel if container exists
  const activeOutlet = appointmentOutlets.find(o => o.id === activeOutletId) || appointmentOutlets[0] || {};
  const carouselContainer = document.getElementById('appointment-carousel-container');
  if (carouselContainer && activeOutlet.images && activeOutlet.images.length > 0) {
    renderCarousel(carouselContainer, activeOutlet.images.map(img => typeof img === 'string' ? { url: img } : img));
  }

  // Event handlers
  (window as any).switchAppointmentOutlet = (id: string) => {
    activeOutletId = id;
    const cur = appointmentOutlets.find(o => o.id === id);
    if (cur?.items?.[0]) selectedItemCode = cur.items[0].code;
    else if (cur?.treatments?.[0]) selectedItemCode = cur.treatments[0].code;
    (window as any).renderApp?.();
  };

  (window as any).selectAppointmentTimeSlot = (slot: string) => {
    selectedTimeSlot = slot;
    document.querySelectorAll('[id^="appointment-time-chip-"]').forEach((el: any) => {
      el.style.border = '1px solid #e0e0e0';
      el.style.background = '#fff';
      el.style.color = '#333';
    });
    const activeChip = document.getElementById(`appointment-time-chip-${slot.replace(':', '_')}`);
    if (activeChip) {
      activeChip.style.border = '1px solid #0284c7';
      activeChip.style.background = '#0284c7';
      activeChip.style.color = '#fff';
    }
  };

  (window as any).selectAppointmentItem = (code: string) => {
    selectedItemCode = code;
    document.querySelectorAll('.appointment-item-card').forEach((el: any) => {
      const isCur = el.getAttribute('data-code') === code;
      el.style.border = isCur ? '1.5px solid #0284c7' : '1px solid #e0e0e0';
      el.style.background = isCur ? '#f0f9ff' : '#fff';
    });
    updatePriceDisplay(serviceId);
  };

  (window as any).changeAppointmentPax = (delta: number) => {
    paxCount = Math.max(1, Math.min(10, paxCount + delta));
    const paxEl = document.getElementById('appointment-pax-display');
    if (paxEl) paxEl.textContent = String(paxCount);
    updatePriceDisplay(serviceId);
  };

  (window as any).proceedAppointmentToGuestInfo = () => {
    const lang = getCurrentLanguage();
    const activeOutlet = appointmentOutlets.find(o => o.id === activeOutletId) || appointmentOutlets[0] || {};
    const items = activeOutlet.items || activeOutlet.treatments || DEFAULT_ITEMS;
    const item = items.find(i => i.code === selectedItemCode) || items[0] || DEFAULT_ITEMS[0];
    const total = (item.price || 0) * paxCount;

    setState({
      bookingType: 'APPOINTMENT',
      engine: 'appointment',
      activeServiceId: serviceId,
      resourceId: activeOutlet.id || undefined,
      resourceName: getLocalizedText(activeOutlet.name, lang, getLocalizedText(service.label, lang, 'Appointment')),
      spaDetails: {
        outletId: activeOutlet.id || 'outlet-1',
        outletName: activeOutlet.name || service.label,
        treatmentCode: item.code,
        treatmentName: item.name,
        bookingDate: selectedDateStr,
        timeSlot: selectedTimeSlot,
        durationMinutes: item.duration || 60,
        pax: paxCount,
      },
      customFields: {
        vatType: item.vatType || 'vat',
        serviceChargeType: item.serviceChargeType || 'non_service',
      },
      selectedRoom: {
        roomTypeCode: item.code,
        roomTypeName: getLocalizedText(item.name, lang, item.code),
        ratePlanCode: 'PAY_AT_VENUE',
        ratePlanName: lang === 'th' ? 'ชำระ ณ สถานที่บริการ (Pay at Venue)' : 'Pay at Venue',
        totalRate: total,
        ratePerRoom: total,
        averageNightlyRate: total,
        totalBeforeTax: item.vatType === 'non_vat' ? total : Math.round(total / 1.07),
        numberOfRooms: 1,
        currency: 'THB',
        available: 1,
        description: `${selectedDateStr} ${selectedTimeSlot} (${paxCount} ${t('guests')})`,
      },
    });

    navigateTo('guest-info');
  };

  // Setup Litepicker
  const trigger = document.getElementById('appointment-date-trigger');
  const container = document.getElementById('appointment-picker-container');
  const toggleIcon = document.getElementById('appointment-date-toggle-icon');
  if (trigger && container) {
    trigger.addEventListener('click', () => {
      container.classList.toggle('collapsed');
      if (toggleIcon) {
        toggleIcon.style.transform = container.classList.contains('collapsed') ? '' : 'rotate(180deg)';
      }
    });

    new Litepicker({
      element: container,
      inlineMode: true,
      singleMode: true,
      minDate: new Date(),
      startDate: selectedDateStr,
      format: 'YYYY-MM-DD',
      setup: (picker) => {
        picker.on('selected', (date) => {
          if (date) {
            selectedDateStr = date.format('YYYY-MM-DD');
            const disp = document.getElementById('appointment-date-display');
            if (disp) disp.textContent = selectedDateStr;
            container.classList.add('collapsed');
            if (toggleIcon) toggleIcon.style.transform = '';
          }
        });
      },
    });
  }

  window.handleBack = () => {
    navigateTo('search');
  };

  initBackToTop('appointment');
}

function updatePriceDisplay(serviceId: string): void {
  const activeOutlet = appointmentOutlets.find(o => o.id === activeOutletId) || appointmentOutlets[0] || {};
  const items = activeOutlet.items || activeOutlet.treatments || DEFAULT_ITEMS;
  const item = items.find(i => i.code === selectedItemCode) || items[0] || DEFAULT_ITEMS[0];
  const total = (item.price || 0) * paxCount;
  const el = document.getElementById('appointment-total-price');
  if (el) {
    el.textContent = total > 0 ? `฿${total.toLocaleString()}` : (getCurrentLanguage() === 'th' ? 'ฟรี' : 'Free');
  }
}
